// Copyright (c) Bentley Systems
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, ComboBox, Input, ProgressRadial, type SelectOption, Text } from "@itwin/itwinui-react";
import { appendLoadMoreOption, LOAD_MORE_VALUE, paginatedItemRenderer, searchPlaceholder } from "../Helpers/ComboBoxHelpers";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import { useITwinProjects } from "../Helpers/useITwinProjects";
import { ITwin } from "../SubscribedProjects/Models/itwin";
import { CatalogGeneralInfo } from "../ProjectManagement/Models/catalog-general-info";
import TextAreaComponent from "../CustomInputComponents/TextInputComponents/TextAreaComponent";
import { EfFieldValidationTypeEnum } from "../Helpers/enumerators";
import { useCatalogProjects } from "./useCatalogProjects";
import { EndpointMode, SignalRMode, useApiExecute } from "./useApiExecute";

const DEFAULT_TIMEOUT_SECS = 120;

/** Renders the response string with per-line colour coding. */
const renderColoredResponse = (text: string) =>
    text.split("\n").map((line, i) => {
        if (line === "---") {
            return (
                <hr
                    key={i}
                    style={{
                        border: "none",
                        borderTop: "1px solid var(--iui-color-border-subtle)",
                        margin: "6px 0",
                    }}
                />
            );
        }
        const isTimeout = line.startsWith("[Timeout");
        const isError = line.startsWith("Error:");
        const isSuccess = line === "Success" || line === "Success (large result fetched)";
        const isProcessing = line.startsWith("[Processing") || line.startsWith("[Still processing") || line.startsWith("[Fetching") || line.startsWith("[Large result");
        const isClosed = line.includes("connection closed");
        const isReconnect = line.includes("reconnect");
        const isWaiting = line.startsWith("Waiting for");
        return (
            <div
                key={i}
                style={{
                    color:
                        isTimeout || isError
                            ? "var(--iui-color-text-negative)"
                            : isSuccess
                            ? "var(--iui-color-text-positive)"
                            : isProcessing
                            ? "var(--iui-color-text-warning)"
                            : isClosed
                            ? "var(--iui-color-text-muted)"
                            : isReconnect
                            ? "var(--iui-color-text-warning)"
                            : "inherit",
                    fontStyle: isWaiting ? "italic" : undefined,
                    fontWeight: isTimeout || isError || isSuccess ? "bold" : undefined,
                }}
            >
                {line || "\u00A0"}
            </div>
        );
    });

const ApiTesting = () => {
    const dispatch = useAppDispatch();
    const accessToken = useAppSelector(selectAccessToken);

    // Use the iTwin projects hook
    const {
        projects,
        selectedProject,
        isProjectsLoading,
        nextLink,
        fetchNextProjects,
        handleSearchChange: projectSearchOnChange,
        handleProjectSelect,
        selectOptions,
        emptyContent,
    } = useITwinProjects({
        isEnabled: true, // Always enabled for standalone mode
        accessToken,
        dispatch,
    });

    // Track the effective iTwinId from selected project
    const iTwinId = useMemo(
        () => selectedProject?.id,
        [selectedProject]
    );

    // Catalog projects hook
    const {
        catalogProjects,
        selectedCatalogProject,
        handleCatalogProjectSelect,
        isCatalogProjectsLoading,
        catalogComboboxInputChange,
        catalogProjectSelectOptions,
        catalogEmptyContent,
        catalogTotalCount,
        fetchMoreCatalogProjects,
        requestBody,
        setRequestBody,
        requestBodyKey,
    } = useCatalogProjects({
        iTwinId,
        accessToken,
        dispatch,
    });

    // Endpoint mode state
    const [endpointMode, setEndpointMode] = useState<EndpointMode>("core");

    // SignalR mode state
    const [signalRMode, setSignalRMode] = useState<SignalRMode>("signalr");
    const [signalRTimeoutSecs, setSignalRTimeoutSecs] = useState(DEFAULT_TIMEOUT_SECS);

    // Endpoint mode options
    const endpointModeOptions = useMemo((): SelectOption<EndpointMode>[] => [
        { value: "core", label: "Core (new)" },
        { value: "legacy", label: "Legacy (old)" },
    ], []);

    // SignalR mode options
    const signalRModeOptions = useMemo((): SelectOption<SignalRMode>[] => [
        { value: "signalr", label: "signalr" },
        { value: "no signalr", label: "no signalr" },
    ], []);

    // API execute hook
    const {
        isSending,
        isSignalRConnected,
        response,
        activityId,
        handleSend,
        handleSendLegacy,
        pendingJobId,
        fetchJobResult,
    } = useApiExecute({
        iTwinId,
        selectedProjectUniqueName: selectedCatalogProject?.UniqueName,
        accessToken,
        requestBody,
        signalRMode,
        signalRTimeoutMs: signalRTimeoutSecs * 1_000,
    });

    // Total elapsed time — recorded when isSending transitions to false
    const sendStartRef = useRef<number | null>(null);
    const [totalElapsedMs, setTotalElapsedMs] = useState<number | null>(null);
    useEffect(() => {
        if (isSending) {
            sendStartRef.current = performance.now();
            setTotalElapsedMs(null);
        } else if (sendStartRef.current !== null) {
            setTotalElapsedMs(performance.now() - sendStartRef.current);
            sendStartRef.current = null;
        }
    }, [isSending]);

    // Countdown timer — ticks every second while a SignalR hub connection is open
    const [countdown, setCountdown] = useState(DEFAULT_TIMEOUT_SECS);
    useEffect(() => {
        setCountdown(signalRTimeoutSecs);
      if (!isSignalRConnected) { return; }
        const interval = setInterval(() => {
            setCountdown((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [isSignalRConnected, signalRTimeoutSecs]);

    // Count-up timer — ticks every second while a non-signalR request is in flight
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        setElapsed(0);
        if (!isSending || (endpointMode === "core" && signalRMode === "signalr")) { return; }
        const interval = setInterval(() => {
            setElapsed((prev) => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [isSending, signalRMode]);

    const coloredResponse = useMemo(() => renderColoredResponse(response), [response]);

    return (
        <div>
            <div className="becs-infopanelbody-footer-line">
                <div className="becs-all-margin">
                    <div className="rowWrap">
                        <Text variant="title">{"API Testing"}</Text>
                    </div>
                </div>
            </div>

            <div className="becs-ltr-margin">
                <div className="horizontal becs-tb-margin">
                    <div
                        className="becs-input-label"
                        style={{ fontWeight: 400 }}
                    >
                        Select iTwin project (showing {projects.length})
                    </div>

                    <ComboBox
                        emptyStateMessage={emptyContent}
                        options={appendLoadMoreOption(selectOptions, !!nextLink)}
                        onChange={(selected: ITwin | typeof LOAD_MORE_VALUE) => {
                            if (selected !== LOAD_MORE_VALUE) {
                                handleProjectSelect(selected);
                            }
                        }}
                        inputProps={{
                            placeholder: searchPlaceholder("Search iTwin project", 3, isProjectsLoading),
                            onChange: projectSearchOnChange,
                        }}
                        filterFunction={(options) => options}
                        value={selectedProject}
                        itemRenderer={paginatedItemRenderer<ITwin>({
                            onLoadMore: fetchNextProjects,
                            isLoading: isProjectsLoading,
                        })}
                    />
                </div>

                <div className="horizontal becs-tb-margin">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <div
                            className="becs-input-label"
                            style={{ fontWeight: 400 }}
                        >
                            Select catalog project (showing {catalogProjects.length} of {catalogTotalCount})
                        </div>
                        {isCatalogProjectsLoading && (
                            <ProgressRadial
                                indeterminate
                                size="x-small"
                                style={{ marginLeft: 8 }}
                            />
                        )}
                    </div>

                    <ComboBox
                        inputProps={{
                            disabled: !iTwinId,
                            placeholder: searchPlaceholder("Search catalog project", 3),
                            onChange: catalogComboboxInputChange,
                        }}
                        filterFunction={(options) => options}
                        emptyStateMessage={catalogEmptyContent}
                        options={appendLoadMoreOption(catalogProjectSelectOptions, catalogTotalCount > catalogProjects.length)}
                        onChange={(selected: CatalogGeneralInfo | typeof LOAD_MORE_VALUE) => {
                            if (selected !== LOAD_MORE_VALUE) {
                                handleCatalogProjectSelect(selected);
                            }
                        }}
                        value={selectedCatalogProject}
                        itemRenderer={paginatedItemRenderer<CatalogGeneralInfo>({
                            onLoadMore: fetchMoreCatalogProjects,
                            isLoading: isCatalogProjectsLoading,
                        })}
                    />
                </div>

                <div className="horizontal becs-tb-margin">
                    <div className="rowWrap" style={{ alignItems: "flex-end", gap: 16 }}>
                        <div>
                            <div
                                className="becs-input-label"
                                style={{ fontWeight: 400, marginBottom: 4 }}
                            >
                                Endpoint
                            </div>
                            <ComboBox
                                options={endpointModeOptions}
                                value={endpointMode}
                                onChange={(selected: EndpointMode) => {
                                    setEndpointMode(selected);
                                }}
                            />
                        </div>
                        {endpointMode === "core" && (
                            <div>
                                <div
                                    className="becs-input-label"
                                    style={{ fontWeight: 400, marginBottom: 4 }}
                                >
                                    SignalR mode
                                </div>
                                <ComboBox
                                    options={signalRModeOptions}
                                    value={signalRMode}
                                    onChange={(selected: SignalRMode) => {
                                        setSignalRMode(selected);
                                    }}
                                />
                            </div>
                        )}
                        {endpointMode === "core" && signalRMode === "signalr" && (
                            <div>
                                <label
                                    htmlFor="signalr-timeout-input"
                                    className="becs-input-label"
                                    style={{ fontWeight: 400, marginBottom: 4 }}
                                >
                                    Timeout (seconds)
                                </label>
                                <Input
                                    id="signalr-timeout-input"
                                    type="number"
                                    min={1}
                                    max={600}
                                    style={{ width: 90 }}
                                    value={signalRTimeoutSecs}
                                    onChange={(e) =>
                                        setSignalRTimeoutSecs(Math.max(1, Number(e.target.value)))
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="becs-t-margin">
                    <div className="becs-input-grid">
                        <div className="becs-input-label">
                            Request body (JSON)
                        </div>
                    </div>
                    <TextAreaComponent
                        key={requestBodyKey}
                        validationType={EfFieldValidationTypeEnum.Json}
                        placeholder="Enter text here..."
                        style={{ height: 200 }}
                        defaultValue={requestBody}
                        onChange={(currentValue) => {
                            setRequestBody(currentValue.target.value);
                        }}
                    />
                </div>

                <div className="becs-t-margin" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Button
                        styleType="high-visibility"
                        onClick={endpointMode === "legacy" ? handleSendLegacy : handleSend}
                        disabled={isSending || !iTwinId || !selectedCatalogProject}
                    >
                        <div>{isSending ? "Sending..." : "Send"}</div>
                    </Button>
                    {pendingJobId && endpointMode === "core" && signalRMode !== "signalr" && (
                        <Button
                            styleType="default"
                            onClick={fetchJobResult}
                            disabled={isSending}
                            title={`Poll job ${pendingJobId}`}
                        >
                            Fetch result
                        </Button>
                    )}
                    {isSending && endpointMode === "core" && signalRMode === "signalr" && (
                        <>
                            <ProgressRadial indeterminate size="x-small" />
                            <span style={{ color: "var(--iui-color-text-muted)", fontSize: "var(--iui-font-size-1)" }}>
                                Waiting for SignalR response
                                {" — "}
                                <span
                                    style={{
                                        color: countdown <= 10
                                            ? "var(--iui-color-text-negative)"
                                            : countdown <= 30
                                            ? "var(--iui-color-text-warning)"
                                            : "var(--iui-color-text-muted)",
                                        fontWeight: countdown <= 10 ? "bold" : undefined,
                                    }}
                                >
                                    {countdown}s
                                </span>
                                {" remaining..."}
                            </span>
                        </>
                    )}
                    {isSending && (endpointMode !== "core" || signalRMode !== "signalr") && (
                        <>
                            <ProgressRadial indeterminate size="x-small" />
                            <span style={{ color: "var(--iui-color-text-muted)", fontSize: "var(--iui-font-size-1)" }}>
                                {"Waiting for response — "}
                                <span>{elapsed}s</span>
                                {" elapsed..."}
                            </span>
                        </>
                    )}
                </div>

                <div className="becs-t-margin">
                    <div className="becs-input-grid">
                        <div className="becs-input-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            Response
                            {totalElapsedMs !== null && !isSending && (
                                <span style={{ fontWeight: 400, fontSize: "var(--iui-font-size-0)", color: "var(--iui-color-text-muted)" }}>
                                    ({totalElapsedMs < 1000
                                        ? `${Math.round(totalElapsedMs)}ms`
                                        : `${(totalElapsedMs / 1000).toFixed(2)}s`})
                                </span>
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            height: 200,
                            overflowY: "auto",
                            resize: "vertical",
                            background: "var(--iui-color-background-backdrop)",
                            border: "1px solid var(--iui-color-border)",
                            borderRadius: "var(--iui-border-radius-1)",
                            padding: "8px 12px",
                            fontFamily: "monospace",
                            fontSize: "var(--iui-font-size-1)",
                            lineHeight: 1.6,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                        }}
                    >
                        {coloredResponse}
                    </div>
                    {activityId && (
                        <div
                            style={{
                                marginTop: 4,
                                fontSize: "var(--iui-font-size-0)",
                                color: "var(--iui-color-text-muted)",
                                fontFamily: "monospace",
                            }}
                        >
                            Activity ID: {activityId}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ApiTesting;